import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

export const BigcommerceOrderCountsCard = memo(
  function BigcommerceOrderCountsCard({ teamId }: { teamId: string }) {
    const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
    const now = new Date();
    const [startYear, setStartYear] = useState(now.getFullYear() - 1);
    const [startMonth] = useState(1);
    const [endYear] = useState(now.getFullYear());
    const [endMonth] = useState(now.getMonth() + 1);

    const [fetchKey, setFetchKey] = useState(0);

    const countsLoad = useLoad(async () => {
      if (fetchKey === 0) {
        return null;
      }
      return rpcClient.getBigcommerceOrderCounts({
        teamId,
        startYear,
        startMonth,
        endYear,
        endMonth,
      });
    }, [teamId, startYear, startMonth, endYear, endMonth, fetchKey]);

    const data = countsLoad.value;

    return (
      <Card title="BigCommerce Order Counts">
        <Flex dir="column" gap="lg" p="lg">
          <Flex align="center" gap="lg">
            <Flex align="center" gap="sm">
              <Text fontSize="sm" fontWeight="medium">
                Start year:
              </Text>
              <select
                onChange={(e) => setStartYear(Number(e.target.value))}
                style={{
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: "1px solid var(--redo-colors-border-border-primary)",
                }}
                value={startYear}
              >
                {Array.from(
                  { length: 5 },
                  (_, i) => now.getFullYear() - 4 + i,
                ).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </Flex>
            <RedoButton
              hierarchy="secondary"
              onClick={() => setFetchKey((k) => k + 1)}
              size="sm"
              text="Fetch"
            />
          </Flex>

          {data?.error && (
            <Text fontSize="sm" textColor="error">
              {data.error}
            </Text>
          )}

          {data?.months && data.months.length > 0 && (
            <Flex dir="column" gap="xs">
              <Flex
                gap="lg"
                style={{
                  borderBottom:
                    "1px solid var(--redo-colors-border-border-primary)",
                  paddingBottom: 4,
                }}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  style={{ width: 100 }}
                >
                  Month
                </Text>
                <Text fontSize="xs" fontWeight="semibold" style={{ width: 80 }}>
                  Orders
                </Text>
              </Flex>
              {data.months.map((m) => (
                <Flex gap="lg" key={`${m.year}-${m.month}`}>
                  <Text fontSize="xs" style={{ width: 100 }}>
                    {m.label}
                  </Text>
                  <Text fontSize="xs" fontWeight="medium" style={{ width: 80 }}>
                    {m.count === -1 ? "Error" : m.count.toLocaleString()}
                  </Text>
                </Flex>
              ))}
              <Flex
                gap="lg"
                style={{
                  borderTop:
                    "1px solid var(--redo-colors-border-border-primary)",
                  paddingTop: 4,
                  marginTop: 4,
                }}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  style={{ width: 100 }}
                >
                  Total
                </Text>
                <Text fontSize="xs" fontWeight="semibold" style={{ width: 80 }}>
                  {data.months
                    .filter((m) => m.count >= 0)
                    .reduce((s, m) => s + m.count, 0)
                    .toLocaleString()}
                </Text>
              </Flex>
            </Flex>
          )}

          {fetchKey > 0 && countsLoad.pending && (
            <Text fontSize="sm" textColor="secondary">
              Loading...
            </Text>
          )}
        </Flex>
      </Card>
    );
  },
);
